import { MAX_IMAGES_PER_MESSAGE } from "./constants";
import { AdminMessageImage } from "./types";

const isPositiveNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value > 0;

const isHttpsUrl = (value: unknown): value is string => {
  if (typeof value !== "string") return false;
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
};

/**
 * Keeps only well-formed https images from an untrusted socket payload. Width
 * and height are kept only as a pair of positive finite numbers — one without
 * the other can't give an aspect ratio — otherwise both are dropped so the
 * bubble falls back to its fallback dimensions instead of a distorted box.
 *
 * @param images - The `images` field as received over the socket (unvalidated).
 * @returns The safe images, or `undefined` when none survive.
 */
export const sanitizeAdminImages = (images: unknown): AdminMessageImage[] | undefined => {
  if (!Array.isArray(images)) return undefined;

  const safe = images
    .filter((image): image is { url: string; width?: unknown; height?: unknown } => isHttpsUrl(image?.url))
    .slice(0, MAX_IMAGES_PER_MESSAGE)
    .map((image) => ({
      url: image.url,
      ...(isPositiveNumber(image.width) &&
        isPositiveNumber(image.height) && { width: image.width, height: image.height }),
    }));
  return safe.length > 0 ? safe : undefined;
};
